import { reviewQueryOptions } from "../api/routes/reviews.ts";
import { REPORT_LIST_PARAM, SHARED_REPORTS_TAB } from "../components/report-sharing/shareForm.ts";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { matchPath, useLocation, useParams, useSearchParams } from "react-router";
import { usePaths } from "../paths.ts";
import { deriveButtonOrigin } from "./insights/useInsightsOriginCrumbs.ts";
import { type RootOrigin, buildRootQuery, getCurrentUrl, getRootOrigin } from "./useOriginCrumbs.ts";

/**
 * Route-derived origin query shared by every "details" destination (workspace details,
 * user details, ...): it only depends on where the link is being built from, not on
 * what it's linking to, so every destination hook can reuse it as-is.
 */
export function useDetailsOriginQuery(): URLSearchParams {
    const paths = usePaths();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { insightId, reviewId } = useParams();

    // The review page doesn't carry its title via the URL, so peek it from the review
    // details query already fetched (and cached) by the review page itself.
    const isReviewsDetail = matchPath(paths.reviewsDetail, location.pathname) !== null;
    const { data: review } = useQuery({
        ...reviewQueryOptions(reviewId ?? ""),
        enabled: isReviewsDetail && !!reviewId
    });

    return useMemo(() => {
        const currentUrl = getCurrentUrl(location);
        const matches = (path: string) => matchPath(path, location.pathname) !== null;

        const isOnAnyReportsRoute = matches(`${paths.reports}/*`);

        // Known titles for routes that don't carry one via `?title=`.
        let reportTitleFromRoute: string | undefined;

        if (matches(paths.reportsUpdate)) {
            reportTitleFromRoute = "Custom report";
        } else if (matches(paths.versionStorageReport)) {
            reportTitleFromRoute = "Version storage";
        } else if (matches(paths.permissionsMatrixSiteSelection)) {
            reportTitleFromRoute = "Site selection";
        } else if (matches(paths.permissionsMatrixReport)) {
            reportTitleFromRoute = "Permissions matrix report";
        } else if (matches(paths.userAccessSelection)) {
            reportTitleFromRoute = "User selection";
        } else if (matches(paths.userAccessReport)) {
            reportTitleFromRoute = "User access report";
        } else if (matches(paths.groupAccessSelection)) {
            reportTitleFromRoute = "Group selection";
        } else if (matches(paths.groupAccessReport)) {
            reportTitleFromRoute = "Group access report";
        } else if (matches(paths.reports) && searchParams.get(REPORT_LIST_PARAM) === SHARED_REPORTS_TAB) {
            reportTitleFromRoute = "Shared reports";
        }

        // Unlike other destinations, this page IS the "insights" origin rather than receiving
        // one, so derive it from the route the same way useInsightsOriginCrumbs's buttonOrigin does.
        const isInsightsObjectList = matches(paths.insightsObjectList);

        // The activity log is also a root-level page rather than a destination that receives
        // an origin, so it derives itself as the origin directly from the route.
        const isActivityLog = matches(paths.activityLog);

        let origin: RootOrigin;

        if (isActivityLog) {
            origin = { kind: "activityLog" };
        } else if (isOnAnyReportsRoute) {
            origin = {
                kind: "report",
                title: searchParams.get("title") ?? reportTitleFromRoute ?? "Report",
                url: currentUrl
            };
        } else if (isReviewsDetail) {
            origin = {
                kind: "review",
                title: review?.title ?? "Review details",
                url: currentUrl
            };
        } else if (isInsightsObjectList) {
            origin = deriveButtonOrigin(getRootOrigin(searchParams), insightId ?? null);
        } else {
            origin = getRootOrigin(searchParams);
        }

        const query = new URLSearchParams(buildRootQuery(origin));

        // The insight's identity is independent of the root origin, so it travels as its
        // own param, same as CreatePolicyButton does.
        if (isInsightsObjectList && insightId) {
            query.set("insightTemplateKey", insightId);
        }

        return query;
    }, [paths, location, searchParams, insightId, isReviewsDetail, review?.title]);
}
